import apiClient from './apiClient';
import { Basket, CreateBasket, UpdateBasket, StrategyMetadata, ApiResponse } from '../types';
import { transformBasket } from '../utils/transformStrategyFields';
import allocationService from './allocationService';
import { withActingAsIfSuperAdmin } from './actingAs';

// All basket/strategy endpoints use the 'strategy' API
const API_TYPE = 'api' as const;

/** Append ?acting_as=<id> ONLY when caller is SuperAdmin. Backend enforces
 *  super_admin_only=True on every method (GET reads + mutations alike); non-
 *  SuperAdmin callers see own data silently rather than hitting 403. Applied
 *  to mutations so SuperAdmin-on-behalf edits land in the target's partition. */
function withQs(path: string): string {
  const qs = withActingAsIfSuperAdmin(new URLSearchParams()).toString();
  return qs ? `${path}?${qs}` : path;
}

class BasketService {
  /**
   * Get all baskets for the authenticated user
   */
  async getBaskets(): Promise<Basket[]> {
    try {
      const response = await apiClient.get<ApiResponse<any[]>>(withQs('/baskets'), undefined, API_TYPE);

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch baskets');
      }

      // Transform backend data to frontend format
      return response.data.map(transformBasket);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch baskets');
    }
  }

  /**
   * Get a specific basket by ID
   */
  async getBasket(basketId: string): Promise<Basket> {
    try {
      const response = await apiClient.get<ApiResponse<any>>(withQs(`/baskets/${basketId}`), undefined, API_TYPE);

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch basket');
      }

      // Transform backend data to frontend format
      return transformBasket(response.data);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch basket');
    }
  }

  /**
   * Create a new basket
   */
  async createBasket(basketData: CreateBasket): Promise<Basket> {
    try {
      // Send data directly - no transformation needed since types match backend
      const apiPayload = {
        name: basketData.basket_name,
        description: basketData.description,
        strategies: basketData.strategies,
        initial_capital: basketData.initial_capital
      };

      const response = await apiClient.post<ApiResponse<Basket>>(
        withQs('/baskets'),
        apiPayload,
        undefined,
        API_TYPE
      );

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to create basket');
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to create basket');
    }
  }

  /**
   * Update a specific basket
   */
  async updateBasket(basketId: string, updates: UpdateBasket): Promise<Basket> {
    try {
      // Send data directly - no transformation needed since types match backend.
      // NOTE: backend's update handler reads `basket_name` (not `name` like create does).
      const apiPayload: any = {};
      if (updates.basket_name !== undefined) apiPayload.basket_name = updates.basket_name;
      if (updates.description !== undefined) apiPayload.description = updates.description;
      if (updates.strategies !== undefined) apiPayload.strategies = updates.strategies;
      if (updates.status !== undefined) apiPayload.status = updates.status;
      if (updates.initial_capital !== undefined) apiPayload.initial_capital = updates.initial_capital;

      const response = await apiClient.put<ApiResponse<Basket>>(
        withQs(`/baskets/${basketId}`),
        apiPayload,
        undefined,
        API_TYPE
      );

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to update basket');
      }

      return transformBasket(response.data);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update basket');
    }
  }

  /**
   * Delete a basket (with validation to prevent deletion if allocations exist)
   */
  async deleteBasket(basketId: string): Promise<void> {
    try {
      // Validation: Check for existing allocations before deletion
      try {
        const allocations = await allocationService.getBasketAllocations(basketId);
        if (allocations.length > 0) {
          throw new Error(
            `Cannot delete basket. Please delete all ${allocations.length} broker allocation${allocations.length === 1 ? '' : 's'} first.`
          );
        }
      } catch (error: any) {
        // If it's our validation error, re-throw it
        if (error.message.includes('Cannot delete basket')) {
          throw error;
        }
        // If it's a 404 or other error (no allocations found), continue with deletion
        console.log('No allocations found for basket or allocation check failed:', basketId);
      }

      // Delete the basket only if validation passes
      const response = await apiClient.delete<ApiResponse>(
        withQs(`/baskets/${basketId}`),
        undefined,
        API_TYPE
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to delete basket');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to delete basket');
    }
  }

  /**
   * Get available strategies for baskets
   */
  async getAvailableStrategies(): Promise<StrategyMetadata[]> {
    try {
      const response = await apiClient.get<ApiResponse<StrategyMetadata[]>>(withQs('/strategies'), undefined, API_TYPE);

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch strategies');
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch strategies');
    }
  }

  /**
   * Associate strategies with a basket
   */
  async associateStrategies(basketId: string, strategyIds: string[]): Promise<Basket> {
    try {
      const response = await apiClient.post<ApiResponse<Basket>>(
        `/baskets/${basketId}/strategies`,
        { strategyIds },
        undefined,
        API_TYPE
      );

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to associate strategies');
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to associate strategies');
    }
  }

  /**
   * Remove strategies from a basket
   */
  async removeStrategies(basketId: string, strategyIds: string[]): Promise<Basket> {
    try {
      const response = await apiClient.delete<ApiResponse<Basket>>(
        `/baskets/${basketId}/strategies`,
        { data: { strategyIds } },
        API_TYPE
      );

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to remove strategies');
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to remove strategies');
    }
  }


  /**
   * Get basket execution status
   */
  async getBasketStatus(basketId: string): Promise<{
    status: 'ACTIVE' | 'PAUSED' | 'INACTIVE';
    lastExecution?: string;
    nextExecution?: string;
    executionCount: number;
  }> {
    try {
      const response = await apiClient.get<ApiResponse<{
        status: 'ACTIVE' | 'PAUSED' | 'INACTIVE';
        lastExecution?: string;
        nextExecution?: string;
        executionCount: number;
      }>>(`/baskets/${basketId}/status`, undefined, API_TYPE);

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch basket status');
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch basket status');
    }
  }

  /**
   * Start/Resume basket execution
   */
  async startBasket(basketId: string): Promise<void> {
    try {
      const response = await apiClient.post<ApiResponse>(
        `/baskets/${basketId}/start`,
        undefined,
        undefined,
        API_TYPE
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to start basket');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to start basket');
    }
  }

  /**
   * Pause basket execution
   */
  async pauseBasket(basketId: string): Promise<void> {
    try {
      const response = await apiClient.post<ApiResponse>(
        `/baskets/${basketId}/pause`,
        undefined,
        undefined,
        API_TYPE
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to pause basket');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to pause basket');
    }
  }

  /**
   * Stop basket execution
   */
  async stopBasket(basketId: string): Promise<void> {
    try {
      const response = await apiClient.post<ApiResponse>(
        `/baskets/${basketId}/stop`,
        undefined,
        undefined,
        API_TYPE
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to stop basket');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to stop basket');
    }
  }

  /**
   * Update basket status (enable/disable)
   */
  async updateBasketStatus(basketId: string, status: 'ACTIVE' | 'INACTIVE' | 'PAUSED'): Promise<Basket> {
    try {
      const response = await apiClient.put<ApiResponse<Basket>>(
        withQs(`/baskets/${basketId}`),
        { status },
        undefined,
        API_TYPE
      );

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to update basket status');
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update basket status');
    }
  }

  /**
   * Reorder user's baskets via the generic /baskets/actions dispatcher.
   * Caller must supply the full set of basket_ids in the desired order —
   * server enforces set-equality.
   */
  async reorderBaskets(basketIds: string[]): Promise<Basket[]> {
    try {
      const response = await apiClient.post<ApiResponse<any[]>>(
        withQs('/baskets/actions'),
        { action: 'reorder', basket_ids: basketIds },
        undefined,
        API_TYPE
      );

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to reorder baskets');
      }

      return response.data.map(transformBasket);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to reorder baskets');
    }
  }

}

const basketService = new BasketService();
export default basketService;
export { BasketService };